import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { Event } from './helpers';
import { TranslatableTextArea } from '../../../Translations/Inputs';
import { getServerErrors } from '../../../../common/helpers';
import { translationsForAttribute } from '../../../Translations/helpers';
import UI from '../../../../common/UI';
import useEditEventMutation from './useEditEventMutation';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useValidation from '../../../../common/useValidation';

const editEventQueueSchema = yup.object();

export interface EditEventQueueFormProps {
  event: Event;
}

const EditEventQueueForm = ({ event }: EditEventQueueFormProps) => {
  const { t } = useTranslation();

  const getValues = (event: Event) => ({
    queue_message: event.queue_message,
    queue_message_translations: translationsForAttribute(event.translations, 'queue_message'),
  });

  const form = useForm({
    values: getValues(event),
  });

  useNavigationPrompt(form.changed());

  const [editEvent, { error, loading }] = useEditEventMutation({
    input: {
      id: event.id,
      queue: form.values,
    },
    onSuccess: (event) => {
      form.reset(getValues(event));
    },
  });

  const { valid, errors } = useValidation({
    schema: editEventQueueSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editEvent()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} sc={{ mb: 4 }} />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>{t('queue')}</UI.Legend>
          <UI.InputDescription>
            {t('event_queue_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.InputGroup sc={{ valid: !errors.queue_message, touched: form.touched.queue_message }}>
          <UI.InputLabel htmlFor="QueueMessage">
            {t('queue_message')}
          </UI.InputLabel>
          <UI.InputDescription>
            {t('queue_message_description')}
          </UI.InputDescription>
          <TranslatableTextArea
            form={form}
            name="queue_message"
            title={t('queue_message')}
            defaultLocale={event.locale}
            id="QueueMessage"
          />
          <UI.ErrorMessages attribute={t('queue_message')} errors={errors.queue_message} />
        </UI.InputGroup>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_event')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditEventQueueForm;
